/**
 * Lobby background animation. Lightweight Canvas-2D effect that runs
 * only while the lobby is on-screen: starfield + streaking particles +
 * additive blending for the GW glow feel. Stops when the game starts so
 * we don't waste cycles competing with the game render loop.
 */

interface Star {
  /** Position in 0..1 (relative to canvas). */
  x: number;
  y: number;
  size: number;
  color: string;
  phase: number;
}

interface Particle {
  /** Position in canvas pixels. */
  x: number;
  y: number;
  vx: number; // canvas px/sec
  vy: number;
  age: number;
  duration: number;
  color: string;
  size: number;
  /** Drawing style. 'streak' = capped line back along velocity (default);
   *  'spark' = single point that fades; 'shooting' = long fast streak. */
  kind: 'streak' | 'spark' | 'shooting';
}

const stars: Star[] = [];
const particles: Particle[] = [];

// Active palette colors used for emit() particle hues. Defaults to
// Synthwave; main.ts overwrites via setLobbyBgPalette() at startup.
const lobbyColors = {
  host: '#ff2bd6',
  guest: '#22e7ff',
  puck: '#fff7a8',
};
export function setLobbyBgPalette(host: string, guest: string, puck: string): void {
  lobbyColors.host = host;
  lobbyColors.guest = guest;
  lobbyColors.puck = puck;
}

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let raf = 0;
let prevTime = 0;
let lastStreakAt = 0;
let lastSparkleAt = 0;
let lastShootingAt = 0;

function ensureStars(): void {
  if (stars.length > 0) return;
  // Matches the game's WebGL starfield distribution — 80% small dust,
  // 15% medium, 5% bright "feature" stars, with five tints so the
  // background reads as a proper nebula rather than just specks.
  const STAR_COUNT = 720;
  for (let i = 0; i < STAR_COUNT; i++) {
    const sizeRoll = Math.random();
    let size: number;
    if (sizeRoll < 0.80) size = 0.4 + Math.random() * 1.2;
    else if (sizeRoll < 0.95) size = 1.2 + Math.random() * 1.4;
    else size = 2.0 + Math.random() * 2.0;

    const tint = Math.random();
    let color: string;
    if (tint < 0.55) color = '#fffaf0';       // warm white
    else if (tint < 0.72) color = '#ff66cc';  // pink
    else if (tint < 0.85) color = '#66e0ff';  // cyan
    else if (tint < 0.93) color = '#ffd866';  // gold
    else color = '#bf8eff';                   // violet

    stars.push({
      x: Math.random(),
      y: Math.random(),
      size,
      color,
      phase: Math.random() * Math.PI * 2,
    });
  }
}

function fitCanvas(): void {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
}

function emitStreak(): void {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  // Streaks rise from the lower-middle band — like embers off the title.
  const x = Math.random() * canvas.width;
  const y = canvas.height * (0.6 + Math.random() * 0.35);
  const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.7;
  const speed = (40 + Math.random() * 80) * dpr;
  const colorRoll = Math.random();
  const color =
    colorRoll < 0.45 ? lobbyColors.host
    : colorRoll < 0.9 ? lobbyColors.guest
    : lobbyColors.puck;
  particles.push({
    kind: 'streak',
    x, y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    age: 0,
    duration: 1.8 + Math.random() * 1.8,
    color,
    size: 0.8 + Math.random() * 1.8,
  });
}

function emitSparkle(): void {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  // Brief twinkle at a random spot — drifts barely, mostly just blooms.
  const colorRoll = Math.random();
  const color =
    colorRoll < 0.5 ? lobbyColors.puck
    : colorRoll < 0.8 ? lobbyColors.guest
    : lobbyColors.host;
  particles.push({
    kind: 'spark',
    x: Math.random() * canvas.width,
    y: Math.random() * canvas.height,
    vx: (Math.random() - 0.5) * 8 * dpr,
    vy: (Math.random() - 0.5) * 8 * dpr,
    age: 0,
    duration: 0.5 + Math.random() * 0.5,
    color,
    size: 1.0 + Math.random() * 1.6,
  });
}

function emitShootingStar(): void {
  if (!canvas) return;
  const dpr = window.devicePixelRatio || 1;
  // Long fast diagonal streak across the canvas. Start near one edge,
  // travel across with a slight downward bias.
  const fromLeft = Math.random() < 0.5;
  const startX = fromLeft ? -50 : canvas.width + 50;
  const startY = Math.random() * canvas.height * 0.6;
  const baseAngle = fromLeft ? 0 : Math.PI;
  const angle = baseAngle + (Math.random() - 0.3) * 0.5;
  const speed = (700 + Math.random() * 400) * dpr;
  const colorRoll = Math.random();
  const color =
    colorRoll < 0.5 ? lobbyColors.puck
    : colorRoll < 0.85 ? lobbyColors.guest
    : lobbyColors.host;
  particles.push({
    kind: 'shooting',
    x: startX,
    y: startY,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    age: 0,
    duration: 1.4 + Math.random() * 0.6,
    color,
    size: 2.5 + Math.random() * 1.5,
  });
}

function frame(now: number): void {
  if (!canvas || !ctx) return;
  raf = requestAnimationFrame(frame);
  const dt = prevTime === 0 ? 0 : Math.min(0.1, (now - prevTime) / 1000);
  prevTime = now;

  // Emit on independent cadences. Streaks frequent, sparkles sprinkled,
  // shooting stars rare-but-dramatic.
  if (now - lastStreakAt > 55) { lastStreakAt = now; emitStreak(); }
  if (now - lastSparkleAt > 90) { lastSparkleAt = now; emitSparkle(); }
  if (now - lastShootingAt > 4500 + Math.random() * 4000) {
    lastShootingAt = now;
    emitShootingStar();
  }

  // Background — solid dark fill (the rink renderer's bg color).
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = '#08020e';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Additive layer for the rest.
  ctx.globalCompositeOperation = 'lighter';

  // Stars.
  const dpr = window.devicePixelRatio || 1;
  for (const s of stars) {
    const intensity = 0.35 + 0.55 * (0.5 + 0.5 * Math.sin(now / 700 + s.phase));
    ctx.fillStyle = s.color;
    ctx.globalAlpha = intensity;
    ctx.beginPath();
    ctx.arc(s.x * canvas.width, s.y * canvas.height, s.size * dpr, 0, Math.PI * 2);
    ctx.fill();
  }

  // Particles — draw each kind appropriately.
  ctx.lineCap = 'round';
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i]!;
    p.age += dt;
    if (p.age >= p.duration || p.y < -50 || p.x < -100 || p.x > canvas.width + 100) {
      particles.splice(i, 1);
      continue;
    }
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    const t = p.age / p.duration;
    const alpha = (1 - t) * 0.9;
    ctx.globalAlpha = alpha;

    if (p.kind === 'spark') {
      // Tiny pulse — radial bloom that contracts as it fades.
      const r = p.size * dpr * (1 - t * 0.4);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }

    // Streak / shooting — capped line from a trail point to current pos.
    const tailLen = p.kind === 'shooting' ? 0.12 : 0.08;
    const tailX = p.x - p.vx * tailLen;
    const tailY = p.y - p.vy * tailLen;
    ctx.strokeStyle = p.color;
    ctx.lineWidth = p.size * dpr * (p.kind === 'shooting' ? 1.6 : 1.2);
    ctx.beginPath();
    ctx.moveTo(tailX, tailY);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    // Shooting stars get a bright leading-edge dot.
    if (p.kind === 'shooting') {
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * dpr * 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.globalAlpha = 1;
}

export function startLobbyBg(c: HTMLCanvasElement): void {
  canvas = c;
  const got = c.getContext('2d');
  if (!got) return;
  ctx = got;
  ensureStars();
  fitCanvas();
  window.addEventListener('resize', fitCanvas);
  if (raf === 0) {
    prevTime = 0;
    raf = requestAnimationFrame(frame);
  }
}

export function stopLobbyBg(): void {
  if (raf !== 0) {
    cancelAnimationFrame(raf);
    raf = 0;
  }
  particles.length = 0;
}
